import { isEmpty, isNil } from "lodash";

import debug from "@plannel/utils/debug-util";
import i18n from "@plannel/utils/i18n";

/*
  AUDIT TRAIL — 변경 이력(diff) 파서.

  서버(AuditLogService.mapDifference)는 한 건의 변경 내역을 아래 토큰이 섞인 "한 줄 문자열"로 내려준다.
    #auditKey#/<필드경로>: (-)<값>                              ← 이전에만 있던 필드(삭제)
    #auditKey#/<필드경로>: (+)<값>                              ← 새로 생긴 필드(추가)
    #auditKey#/<필드경로>: #oldValue#<이전> #newValue#<이후>     ← 값이 바뀐 필드(수정)
  각 항목 뒤에는 공백 한 칸이 붙고, 필드경로는 `/` 로 중첩을 표현한다(`/attrs/0/name`).

  ⚠ 구분자는 공백이 아니라 위 토큰이다.
     예전 화면은 이 문자열을 split(" ") 으로 잘라 썼는데, 값에 공백이 있으면("Seoul Plant 1")
     한 항목이 세 조각으로 흩어져 이전값/이후값 짝이 통째로 깨졌다. 토큰 기준으로만 자를 것.
*/

export const AUDIT_DIFF_TYPE = {
  CHANGED: "changed",
  ADDED: "added",
  REMOVED: "removed",
  UNCHANGED: "unchanged",
};

const AUDIT_KEY_TOKEN = "#auditKey#";
const OLD_VALUE_TOKEN = "#oldValue#";
const NEW_VALUE_TOKEN = " #newValue#";
const ADDED_TOKEN = "(+)";
const REMOVED_TOKEN = "(-)";
const KEY_SEPARATOR = ": ";

// 서버가 diff 에서 빼는 시스템 컬럼(AuditLogService.SYSTEM_FIELDS_TO_EXCLUDE 와 같은 목록).
// "전체 필드 보기"도 같은 기준으로 감춰야 목록과 팝업의 필드 수가 어긋나지 않는다.
const SYSTEM_FIELDS = ["verNum", "updatedTs", "updatedBy", "createdTs", "createdBy"];

// 수정 → 추가 → 삭제 → 변경없음. "무엇이 바뀌었나"가 첫 화면에 오도록 고정한 순서다.
const TYPE_ORDER = {
  [AUDIT_DIFF_TYPE.CHANGED]: 0,
  [AUDIT_DIFF_TYPE.ADDED]: 1,
  [AUDIT_DIFF_TYPE.REMOVED]: 2,
  [AUDIT_DIFF_TYPE.UNCHANGED]: 3,
};

// 서버가 필드경로 조각마다 snake_case → camelCase 로 바꿔 내려주므로(convertPathToCamelCase),
// 원본 JSON 을 직접 펼칠 때도 같은 규칙을 태워야 diff 행과 필드명이 맞물린다.
const snakeToCamelCase = (part) => part.replace(/_(\w)/g, (_match, char) => char.toUpperCase());

// `/item/safety_qty` → `item.safetyQty`
const toFieldName = (path) => path.split("/").filter(part => !isEmpty(part)).map(snakeToCamelCase).join(".");

const isSystemField = (field) => SYSTEM_FIELDS.includes(field.split(".").pop());

const compareRows = (a, b) => (TYPE_ORDER[a.type] - TYPE_ORDER[b.type]) || a.field.localeCompare(b.field);

/*
  토큰 하나를 { field, type, oldValue, newValue } 로.
  값 안에 `: ` 나 공백이 있어도 안전하도록, 키는 "첫 번째" `: ` 까지만 잘라낸다(키는 항상 맨 앞).
*/
const parseEntry = (segment) => {
  const separatorAt = segment.indexOf(KEY_SEPARATOR);
  if (separatorAt < 0) {
    return null;
  }

  const field = toFieldName(segment.slice(0, separatorAt));
  const body = segment.slice(separatorAt + KEY_SEPARATOR.length);
  if (isEmpty(field)) {
    return null;
  }

  if (body.startsWith(REMOVED_TOKEN)) {
    return { field, type: AUDIT_DIFF_TYPE.REMOVED, oldValue: body.slice(REMOVED_TOKEN.length), newValue: "" };
  }

  if (body.startsWith(ADDED_TOKEN)) {
    return { field, type: AUDIT_DIFF_TYPE.ADDED, oldValue: "", newValue: body.slice(ADDED_TOKEN.length) };
  }

  if (body.startsWith(OLD_VALUE_TOKEN)) {
    const newValueAt = body.indexOf(NEW_VALUE_TOKEN);
    if (newValueAt < 0) {
      return { field, type: AUDIT_DIFF_TYPE.CHANGED, oldValue: body.slice(OLD_VALUE_TOKEN.length), newValue: "" };
    }

    return {
      field,
      type: AUDIT_DIFF_TYPE.CHANGED,
      oldValue: body.slice(OLD_VALUE_TOKEN.length, newValueAt),
      newValue: body.slice(newValueAt + NEW_VALUE_TOKEN.length),
    };
  }

  // 알 수 없는 형태 — 값을 버리지 말고 이후값으로 살려 둔다(진단 가능하도록).
  return { field, type: AUDIT_DIFF_TYPE.CHANGED, oldValue: "", newValue: body };
};

/*
  필드명 → 사람이 읽는 라벨.

  diff 의 필드명은 엔티티 필드(camelCase)인데, 이 앱은 그리드 컬럼 라벨을 **같은 이름의 번역 키**로
  관리한다(`lastLoginTs` → "Last Login"). 그러니 그 사전을 그대로 쓴다.
  키가 없으면 원래 이름을 둔다 — 사전에 없는 걸 억지로 다듬으면 어느 필드인지 알 수 없게 된다.

  · 리소스 번들을 **직접** 뒤진다. i18n.t()/exists() 는 `.` 을 키 구분자로 보기 때문에
    중첩 경로(`item.safetyQty`)를 통째로 넘기면 절대 못 찾는다.
    (GridUtils.getColumnDefs 가 헤더를 번역하는 방식과 같다)
  · 중첩 경로는 조각별로 번역한다. 배열 인덱스(숫자)는 그대로 둔다.
  · grid 네임스페이스는 쓰지 않는다 — 거기엔 필드명이 아닌 일반 단어(`fail`·`json`·`equal`)가
    섞여 있어, 이름이 우연히 겹치면 엉뚱한 라벨이 붙는다.
*/
const FIELD_PATH_SEPARATOR = " › ";
const ARRAY_INDEX = /^\d+$/;

export const auditFieldLabel = (field) => {
  if (isEmpty(field)) {
    return "";
  }

  const { translation } = i18n.getDataByLanguage(i18n.resolvedLanguage ?? i18n.language) ?? {};

  return String(field)
    .split(".")
    .map(part => (ARRAY_INDEX.test(part) ? part : (translation?.[part] ?? part)))
    .join(FIELD_PATH_SEPARATOR);
};

/**
 * diffValue 문자열 → 변경된 필드 행 배열.
 * @param {string} diffValue 서버가 내려준 diff 문자열
 * @returns {{field: string, type: string, oldValue: string, newValue: string}[]}
 */
export const parseAuditDiff = (diffValue) => {
  if (isEmpty(diffValue)) {
    return [];
  }

  return diffValue.split(AUDIT_KEY_TOKEN)
    .map(segment => segment.replace(/\s+$/, ""))
    .filter(segment => !isEmpty(segment))
    .map(parseEntry)
    .filter(row => !isNil(row) && !isSystemField(row.field))
    .sort(compareRows);
};

// 중첩 객체/배열을 `/a/b/0` 형태의 평탄 경로로. 서버 FlatMapUtil.flatten 과 같은 규칙.
const flattenRecord = (value, path, flat) => {
  if (Array.isArray(value)) {
    value.forEach((item, index) => flattenRecord(item, `${path}/${index}`, flat));
    return;
  }

  if (!isNil(value) && typeof value === "object") {
    Object.entries(value).forEach(([key, item]) => flattenRecord(item, `${path}/${key}`, flat));
    return;
  }

  flat[toFieldName(path)] = isNil(value) ? "" : String(value);
};

/*
  oldValue / newValue 원본을 필드 맵으로.
  z_audit_log 쪽 레코드는 Java 객체 toString(`{a=1, b=2}`) 으로 저장된 것도 있어 JSON.parse 가 실패한다.
  그때는 null 을 돌려주고, 호출부는 "전체 필드 보기"를 접는다(억지로 파싱해 틀린 값을 보여주지 않는다).
*/
const parseAuditRecord = (jsonString) => {
  if (isEmpty(jsonString)) {
    return null;
  }

  const trimmed = jsonString.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (isNil(parsed) || typeof parsed !== "object") {
      return null;
    }

    const flat = {};
    flattenRecord(parsed, "", flat);
    Object.keys(flat).forEach(field => {
      if (isSystemField(field)) {
        delete flat[field];
      }
    });

    return flat;
  } catch (e) {
    debug(e);
    return null;
  }
};

/**
 * 상세 팝업용 행 목록. 변경된 필드 + (원본 파싱이 가능하면) 그대로인 필드까지.
 * @param {object} auditRow 그리드 행 데이터(diffValue / oldValue / newValue)
 * @returns {{rows: object[], hasUnchanged: boolean}} hasUnchanged=false 면 "변경된 항목만" 토글을 숨긴다
 */
export const buildAuditFieldRows = (auditRow) => {
  const { diffValue, oldValue, newValue } = auditRow ?? {};
  const changedRows = parseAuditDiff(diffValue);

  const oldRecord = parseAuditRecord(oldValue);
  const newRecord = parseAuditRecord(newValue);
  if (isNil(oldRecord) && isNil(newRecord)) {
    return { rows: changedRows, hasUnchanged: false };
  }

  const changedFields = new Set(changedRows.map(row => row.field));
  const allFields = new Set([...Object.keys(oldRecord ?? {}), ...Object.keys(newRecord ?? {})]);
  const unchangedRows = [...allFields]
    .filter(field => !changedFields.has(field))
    .map(field => {
      const value = newRecord?.[field] ?? oldRecord?.[field] ?? "";
      return { field, type: AUDIT_DIFF_TYPE.UNCHANGED, oldValue: value, newValue: value };
    });

  return { rows: [...changedRows, ...unchangedRows].sort(compareRows), hasUnchanged: !isEmpty(unchangedRows) };
};

/**
 * 감사 로그 시각 표기. `2026-08-06T15:56:58.442+00:00` → `2026-08-06 15:56:58`.
 * 밀리초·오프셋은 이 화면에서 아무 판단에도 쓰이지 않는데 컬럼 폭만 잡아먹어 잘라 낸다.
 */
export const formatAuditTimestamp = (value) => (isEmpty(value) ? "" : String(value).replace("T", " ").slice(0, 19));

/**
 * 행 목록 → 붙여넣기용 평문(티켓·메일에 그대로 옮길 수 있게).
 */
export const auditDiffToText = (rows) => (rows ?? []).map(row => {
  const field = auditFieldLabel(row.field);

  if (row.type === AUDIT_DIFF_TYPE.ADDED) {
    return `${field}: (+) ${row.newValue}`;
  }
  if (row.type === AUDIT_DIFF_TYPE.REMOVED) {
    return `${field}: (-) ${row.oldValue}`;
  }
  if (row.type === AUDIT_DIFF_TYPE.UNCHANGED) {
    return `${field}: ${row.newValue}`;
  }

  return `${field}: ${row.oldValue} -> ${row.newValue}`;
}).join("\n");
